import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, useFetch, useMutation } from '../api';

type ListApplications = ApiClient['v1']['workspaces'][':workspaceId']['applications']['$get'];
export type ApplicationListResponse = InferResponseType<ListApplications, 200>;
export type ApplicationResponse = ApplicationListResponse['applications'][number];
export type ApplicationListQuery = NonNullable<InferRequestType<ListApplications>['query']>;

type CreateApplication = ApiClient['v1']['workspaces'][':workspaceId']['applications']['$post'];
export type CreateApplicationRequest = InferRequestType<CreateApplication>['json'];
type CreateApplicationResponse = InferResponseType<CreateApplication, 201>;

export function useApplications(workspaceId: number, params: ApplicationListQuery = {}) {
  return useFetch<ApplicationListResponse>(
    `workspaces/${workspaceId}/applications?${JSON.stringify(params)}`,
    (client) =>
      client.v1.workspaces[':workspaceId'].applications.$get({
        param: { workspaceId: workspaceId.toString() },
        query: params,
      }),
    undefined,
    { keepPreviousData: true }
  );
}

export function useAddApplication(
  workspaceId: number,
  actionOnErr: (error?: unknown) => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/applications`;

  const addApplicationMutation = useMutation<CreateApplicationResponse, CreateApplicationRequest>(
    listCacheKey,
    (client, data) =>
      client.v1.workspaces[':workspaceId'].applications.$post({
        param: { workspaceId: workspaceId.toString() },
        json: data,
      }),
    actionOnErr,
    { suppressToast: true } // suppress toast — error is shown inline in the dialog
  );

  return useCallback(
    async (updates: CreateApplicationRequest) => {
      const result = await addApplicationMutation(updates);
      await mutate(
        (key: unknown) =>
          typeof key === 'string' && (key === listCacheKey || key.startsWith(`${listCacheKey}?`))
      );
      return result;
    },
    [addApplicationMutation, mutate, listCacheKey]
  );
}
